import type { AuthSessionUser } from '../auth/types';
import { fetchWithRetry } from './http';

export const ADMIN_GROUP_NAME = 'Committee Grp';

export interface AdminFeedbackResponse {
  id: string;
  formId: string;
  profileId: string;
  profile?: {
    id: string;
    displayName: string;
    companyId?: string;
    companyName?: string;
    roleType?: string;
  };
  answers: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  submittedAt?: string;
}

export interface AdminFeedbackQuestion {
  id: string;
  type: 'single-choice' | 'multi-choice' | 'text' | 'one-to-x';
  prompt: string;
  order: number;
  required?: boolean;
  options?: Array<{ id: string; label: string }>;
  maxSelections?: number;
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
}

export interface AdminFeedbackForm {
  id: string;
  conferenceId?: string;
  sessionId?: string;
  title: string;
  description?: string;
  status?: string;
  questions: AdminFeedbackQuestion[];
  responseCount: number;
  responses: AdminFeedbackResponse[];
}

export interface AdminQrLanderConfig {
  targetUrl: string;
  updatedAt?: string | null;
  updatedByProfileId?: string;
  updatedByDisplayName?: string;
  updatedByEmail?: string;
}

export interface AdminConferenceSessionAttendance {
  id: string;
  title: string;
  startAt?: string | null;
  endAt?: string | null;
  room?: string;
  attendanceEnabled: boolean;
  planningCount: number;
}

export interface AdminConferenceAttendance {
  id: string;
  name: string;
  year?: number | null;
  startAt?: string | null;
  endAt?: string | null;
  planningCount: number;
  sessions: AdminConferenceSessionAttendance[];
}

export interface AdminUsageStatsTotals {
  posts: number;
  replies: number;
  likes: number;
  profiles: number;
}

export interface AdminUsageStatsMonth extends AdminUsageStatsTotals {
  month: string;
  label: string;
}

export interface AdminUsageStatsCompany {
  companyId: string;
  companyName: string;
  companyType: string;
  profiles: number;
}

export interface AdminUsageStats {
  totals: AdminUsageStatsTotals;
  months: AdminUsageStatsMonth[];
  profilesByCompany: AdminUsageStatsCompany[];
}

export function comparableGroupName(value: unknown) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

export function isCommitteeAdmin(user: AuthSessionUser | null | undefined) {
  const target = comparableGroupName(ADMIN_GROUP_NAME);
  return (user?.groups ?? []).some((group) => comparableGroupName(group.name) === target);
}

async function fetchAdminJson<T>(path: string, label: string): Promise<T> {
  const res = await fetchWithRetry(
    path,
    {
      method: 'GET',
      credentials: 'include'
    },
    { retries: 2 }
  );

  if (res.status === 401) throw new Error('Unauthenticated');
  if (res.status === 403) throw new Error('Admin access required');
  if (!res.ok) throw new Error(`${label} request failed (${res.status})`);

  return res.json() as Promise<T>;
}

export async function fetchAdminFeedbackForms(): Promise<AdminFeedbackForm[]> {
  const payload = await fetchAdminJson<{ forms?: AdminFeedbackForm[] }>(
    '/auth/admin/feedback-responses',
    'Admin feedback'
  );
  return Array.isArray(payload.forms) ? payload.forms : [];
}

export async function fetchAdminQrLanderConfig(): Promise<AdminQrLanderConfig> {
  const payload = await fetchAdminJson<{ config?: AdminQrLanderConfig }>(
    '/auth/admin/qrlander',
    'Admin QR redirect'
  );
  return payload.config ?? { targetUrl: '' };
}

export async function updateAdminQrLanderConfig(targetUrl: string): Promise<{
  config: AdminQrLanderConfig;
  changed: boolean;
}> {
  const res = await fetchWithRetry(
    '/auth/admin/qrlander',
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ targetUrl })
    },
    { retries: 1 }
  );

  if (res.status === 401) throw new Error('Unauthenticated');
  if (res.status === 403) throw new Error('Admin access required');
  if (!res.ok) {
    let message = `Admin QR redirect request failed (${res.status})`;
    try {
      const payload = (await res.json()) as { error?: string };
      if (payload.error) message = payload.error;
    } catch {
      // Keep generic message.
    }
    throw new Error(message);
  }

  const payload = (await res.json()) as { config?: AdminQrLanderConfig; changed?: boolean };
  return {
    config: payload.config ?? { targetUrl },
    changed: payload.changed === true
  };
}

export async function switchToPresenterSignIn(): Promise<AuthSessionUser> {
  const res = await fetchWithRetry(
    '/auth/admin/switch-presenter',
    {
      method: 'POST',
      credentials: 'include'
    },
    { retries: 1 }
  );

  if (res.status === 401) throw new Error('Unauthenticated');
  if (res.status === 403) throw new Error('Admin access required');
  if (!res.ok) throw new Error(`Presenter switch request failed (${res.status})`);

  const payload = (await res.json()) as { authenticated?: boolean; user?: AuthSessionUser };
  if (!payload.authenticated || !payload.user?.sub) throw new Error('Presenter switch response missing user');
  return payload.user;
}

export async function fetchAdminConferenceAttendance(): Promise<AdminConferenceAttendance[]> {
  const payload = await fetchAdminJson<{ conferences?: AdminConferenceAttendance[] }>(
    '/auth/admin/conference-attendance',
    'Admin conference attendance'
  );
  return Array.isArray(payload.conferences) ? payload.conferences : [];
}

export async function fetchAdminUsageStats(): Promise<AdminUsageStats> {
  const payload = await fetchAdminJson<{ stats?: AdminUsageStats }>(
    '/auth/admin/usage-stats',
    'Admin usage stats'
  );

  return payload.stats ?? {
    totals: { posts: 0, replies: 0, likes: 0, profiles: 0 },
    months: [],
    profilesByCompany: []
  };
}
